/**
 * 週間サマリーコンポーネント
 *
 * 過去7日間の達成状況をカレンダー形式で表示する。
 * 各曜日の完了数を円形のバッジで視覚化。
 *
 * Rails で言えば:
 * - app/views/dashboard/_weekly_summary.html.erb に相当
 *
 * @example
 * <WeeklySummary
 *   weeklyStats={stats}
 *   today="2024-01-15"
 * />
 */

import { Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WEEKDAY_LABELS } from '@/types'
import type { WeeklyStat } from '@/types'

interface WeeklySummaryProps {
  /** 週間統計データ（7日分） */
  weeklyStats: WeeklyStat[]
  /** 今日の日付（"YYYY-MM-DD" 形式） */
  today: string
}

export function WeeklySummary({ weeklyStats, today }: WeeklySummaryProps) {
  return (
    <div className="bg-card border-border rounded-xl border p-4 sm:p-5">
      <h2 className="text-foreground mb-4 flex items-center gap-2 font-semibold">
        <Calendar className="text-primary h-5 w-5" />
        週間サマリー
      </h2>

      <div className="flex justify-between gap-1 sm:gap-2">
        {weeklyStats.map((stat) => {
          // 達成率を計算（予定がない日は 0%）
          const percent =
            stat.scheduled > 0 ? (stat.completed / stat.scheduled) * 100 : 0
          const isToday = stat.date === today

          return (
            <div key={stat.date} className="flex-1 text-center">
              {/* 曜日ラベル */}
              <div className="text-muted-foreground mb-2 text-xs">
                {WEEKDAY_LABELS[stat.dayOfWeek]}
              </div>

              {/* 完了数バッジ */}
              <div
                className={cn(
                  'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors sm:h-10 sm:w-10 sm:text-sm',
                  // 今日はリング装飾
                  isToday && 'ring-primary ring-2 ring-offset-2',
                  // 達成率に応じた背景色
                  percent === 100
                    ? 'bg-primary text-primary-foreground' // 100%: 塗りつぶし
                    : percent > 0
                      ? 'bg-primary/30 text-primary' // 一部完了: 薄い色
                      : 'bg-muted text-muted-foreground' // 未完了: グレー
                )}
              >
                {stat.completed}
              </div>

              {/* 予定数（デスクトップのみ表示） */}
              <div className="text-muted-foreground mt-1 hidden text-xs sm:block">
                /{stat.scheduled}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
